import UpdateIcon from '@mui/icons-material/Update'
import UpdateDisabledIcon from '@mui/icons-material/UpdateDisabled'
import WarningRoundedIcon from '@mui/icons-material/WarningRounded'
import dayjs, { Dayjs } from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import isBetween from 'dayjs/plugin/isBetween'
import quarterOfYear from 'dayjs/plugin/quarterOfYear'
import utc from 'dayjs/plugin/utc'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Tooltip } from 'sutra/components/Tooltip/Tooltip'

import { CascadingDateMenu } from 'components/date-range-picker/CascadingDateMenu/CascadingDateMenu'
import {
  ALL_TIME_START_DATE,
  DEFAULT_DATE_SHORTHAND,
  formatDateRangeLabel,
  SHORTHAND_DATE_OPTIONS,
  XTD_DATE_OPTIONS,
} from 'components/date-range-picker/date-defaults'
import {
  DatePopover,
  MostRecentEnableLink,
  MostRecentStatusIcon,
  TooltipAnchor,
  TooltipLines,
  TriggerButton,
  TriggerChevron,
  UnsyncedWarningIcon,
} from 'components/date-range-picker/DateRangeSelector/styles'
import type {
  DateRangePresetSource,
  DateSelectionMeta,
  PriorPeriodEntry,
  PriorPeriodGroup,
} from 'components/date-range-picker/types'
import {
  utcEndOfDay,
  utcStartOfDay,
  wallClockToUtc,
} from 'components/date-range-picker/utc-date-utils'

dayjs.extend(customParseFormat)
dayjs.extend(isBetween)
dayjs.extend(quarterOfYear)
dayjs.extend(utc)

interface DateRangeSelectorProps {
  dateRange: {
    startDate: Date | null
    endDate: Date | null
  }
  onDateRangeChange: (
    startDate: Date,
    endDate: Date,
    source?: DateRangePresetSource,
    presetKey?: string | null,
    meta?: DateSelectionMeta,
  ) => void
  disabled?: boolean

  // ── Sync to dashboard (optional; row hidden unless onSync is given) ────
  onSync?: () => void
  /** Whether the current selection matches the parent/dashboard date.
   * Defaults to true (synced) when `onSync` is given but this is omitted. */
  isSynced?: boolean

  // ── Most recent toggle (optional; row hidden unless this is defined) ───
  mostRecent?: boolean
  /** When given, the toggle is disabled unless the active preset is in this
   * list (in addition to being disabled for Custom / Prior Period / "All time"). */
  mostRecentEligiblePresetKeys?: readonly string[]
  /** The caller's own known-active preset (e.g. a dashboard's `timeResolution`
   * mapped back to a preset key while "Most recent" is rolling). When given
   * and non-null, this wins over date-based `detectDateRangeType` — a rolling
   * "Most recent" window rarely lands on the exact date the preset's own math
   * would produce (calendar-month vs. exact-day arithmetic), which would
   * otherwise misdetect as `custom` and disable the toggle. */
  presetKey?: string | null

  // ── Prior Period cascade (optional; row hidden when empty) ─────────────
  priorPeriods?: readonly PriorPeriodGroup[]
}

export const DateRangeSelector: React.FC<DateRangeSelectorProps> = ({
  dateRange,
  onDateRangeChange,
  disabled,
  onSync,
  isSynced,
  mostRecent,
  mostRecentEligiblePresetKeys,
  presetKey,
  priorPeriods,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  // Track how the date was selected to avoid cross-highlighting
  // Default to the configured default date range
  const [selectionSource, setSelectionSource] = useState<
    DateRangePresetSource | 'priorPeriod' | null
  >('shorthand')
  const [selectedValue, setSelectedValue] = useState<string | null>(
    DEFAULT_DATE_SHORTHAND,
  )

  // Track whether the last date change was from an internal selection
  // to avoid re-detecting when we already know the source/value
  const isInternalChangeRef = useRef(false)

  const shorthandOptions = SHORTHAND_DATE_OPTIONS

  // Calculate date range for different modes, in UTC.
  const calculateDateRange = useCallback(
    (mode: string, value?: number, unit?: string, since?: Dayjs) => {
      const now = dayjs.utc()

      switch (mode) {
        case 'today':
          return {
            startDate: utcStartOfDay(now).toDate(),
            endDate: utcEndOfDay(now).toDate(),
          }
        case 'yesterday': {
          const yesterday = now.subtract(1, 'day')
          return {
            startDate: utcStartOfDay(yesterday).toDate(),
            endDate: utcEndOfDay(yesterday).toDate(),
          }
        }
        case '7d':
          return {
            startDate: utcStartOfDay(now.subtract(7, 'day')).toDate(),
            endDate: utcEndOfDay(now).toDate(),
          }
        case '30d':
          return {
            startDate: utcStartOfDay(now.subtract(30, 'day')).toDate(),
            endDate: utcEndOfDay(now).toDate(),
          }
        case '3m':
          return {
            startDate: utcStartOfDay(now.subtract(3, 'month')).toDate(),
            endDate: utcEndOfDay(now).toDate(),
          }
        case '6m':
          return {
            startDate: utcStartOfDay(now.subtract(6, 'month')).toDate(),
            endDate: utcEndOfDay(now).toDate(),
          }
        case '12m':
          return {
            startDate: utcStartOfDay(now.subtract(12, 'month')).toDate(),
            endDate: utcEndOfDay(now).toDate(),
          }
        case '2y':
          return {
            startDate: utcStartOfDay(now.subtract(2, 'year')).toDate(),
            endDate: utcEndOfDay(now).toDate(),
          }
        case 'all':
          return {
            startDate: ALL_TIME_START_DATE,
            endDate: utcEndOfDay(now).toDate(),
          }
        case 'wtd': // Week to Date
          return {
            startDate: now.startOf('week').toDate(),
            endDate: utcEndOfDay(now).toDate(),
          }
        case 'mtd': // Month to Date
          return {
            startDate: now.startOf('month').toDate(),
            endDate: utcEndOfDay(now).toDate(),
          }
        case 'qtd': // Quarter to Date
          return {
            startDate: now.startOf('quarter').toDate(),
            endDate: utcEndOfDay(now).toDate(),
          }
        case 'ytd': // Year to Date
          return {
            startDate: now.startOf('year').toDate(),
            endDate: utcEndOfDay(now).toDate(),
          }
        case 'last':
          if (value && unit) {
            return {
              startDate: utcStartOfDay(
                now.subtract(value, unit as dayjs.ManipulateType),
              ).toDate(),
              endDate: utcEndOfDay(now).toDate(),
            }
          }
          break
        case 'since':
          if (since) {
            // The only non-UTC value in the picker: antd's <DatePicker> always
            // emits a local-mode dayjs. Reinterpret its calendar day as UTC so
            // picking a day stores that day's UTC midnight regardless of offset.
            return {
              startDate: utcStartOfDay(wallClockToUtc(since)).toDate(),
              endDate: utcEndOfDay(now).toDate(),
            }
          }
          break
      }

      // Default fallback
      return {
        startDate: utcStartOfDay(now.subtract(6, 'month')).toDate(),
        endDate: utcEndOfDay(now).toDate(),
      }
    },
    [],
  )

  // Helper function to detect if the incoming dateRange matches a known preset
  const detectDateRangeType = useCallback(
    (
      startDate: Date | null,
      endDate: Date | null,
    ): {
      source: DateRangePresetSource | 'priorPeriod'
      value: string | null
    } => {
      if (!startDate || !endDate) {
        return { source: 'custom', value: null }
      }

      // All comparisons are UTC-mode: the stored Dates encode UTC instants and
      // calculateDateRange emits UTC-anchored Dates, so both sides of every
      // isSame('day') must be read in UTC or boundary days mis-detect.
      const start = dayjs(startDate).utc()
      const end = dayjs(endDate).utc()
      const now = dayjs.utc()

      // Helper to check if two dates are close (within 1 day tolerance for end-of-day differences)
      const isSameDay = (date1: Dayjs, date2: Dayjs): boolean => {
        return date1.isSame(date2, 'day')
      }

      // Prior Period entries rarely end today (e.g. "July 2024"), so check
      // them before the endsToday-gated preset scan below.
      if (priorPeriods) {
        for (const group of priorPeriods) {
          for (const entry of group.entries) {
            if (
              isSameDay(start, dayjs(entry.startDate).utc()) &&
              isSameDay(end, dayjs(entry.endDate).utc())
            ) {
              return { source: 'priorPeriod', value: entry.key }
            }
          }
        }
      }

      // Check if end date is today (most presets end today)
      const endsToday = isSameDay(end, now.endOf('day'))

      if (endsToday) {
        // Check shorthand options
        for (const option of SHORTHAND_DATE_OPTIONS) {
          const calculated = calculateDateRange(option.key)
          const calculatedStart = dayjs(calculated.startDate).utc()

          if (isSameDay(start, calculatedStart)) {
            return { source: 'shorthand', value: option.key }
          }
        }

        // Check XTD options
        for (const option of XTD_DATE_OPTIONS) {
          const calculated = calculateDateRange(option.key)
          const calculatedStart = dayjs(calculated.startDate).utc()

          if (isSameDay(start, calculatedStart)) {
            return { source: 'xtd', value: option.key }
          }
        }
      }

      // If no match found, it's a custom date range
      return { source: 'custom', value: null }
    },
    [calculateDateRange, priorPeriods],
  )

  // Sync selection state from dateRange prop
  // This ensures that when a new panel is created with a duplicated config,
  // the date selector displays the correct type (shorthand/xtd/custom)
  // Skip detection if the change was from an internal selection (we already set the values)
  useEffect(() => {
    if (isInternalChangeRef.current) {
      // Reset the flag for the next change
      isInternalChangeRef.current = false
      return
    }

    // A caller-known preset (a rolling "Most recent" resolution) wins over
    // date-based detection — see the `presetKey` prop doc.
    if (presetKey) {
      if (SHORTHAND_DATE_OPTIONS.some((o) => o.key === presetKey)) {
        setSelectionSource('shorthand')
        setSelectedValue(presetKey)
        return
      }
      if (XTD_DATE_OPTIONS.some((o) => o.key === presetKey)) {
        setSelectionSource('xtd')
        setSelectedValue(presetKey)
        return
      }
    }

    const detected = detectDateRangeType(dateRange.startDate, dateRange.endDate)
    setSelectionSource(detected.source)
    setSelectedValue(detected.value)
  }, [dateRange.startDate, dateRange.endDate, presetKey, detectDateRangeType])

  // Handle shorthand button selection (7D, 30D, etc.)
  const handleDateChange = useCallback(
    (
      startDate: Date,
      endDate: Date,
      source: DateRangePresetSource,
      value?: string,
    ) => {
      // Mark this as an internal change to skip re-detection in the effect
      // This avoids the day-boundary race condition where detectDateRangeType
      // uses a different dayjs() than calculateDateRange
      isInternalChangeRef.current = true
      setSelectionSource(source)
      setSelectedValue(value || null)
      // Preserve the "Most recent" intent across a preset re-pick (e.g. the
      // toggle was on for "Last 6 months" and the user clicks "Last 3
      // months") — never for Custom or "All time". The consumer is the
      // authority on whether the new preset actually resolves to a
      // recomputing selection; an ineligible pick round-trips back through
      // the `mostRecent` prop as false on the next render.
      const meta: DateSelectionMeta = {
        mostRecent:
          source !== 'custom' && value !== 'all' && Boolean(mostRecent),
      }
      onDateRangeChange(startDate, endDate, source, value ?? null, meta)
      // Static presets (shorthand / xtd) are single-click terminal selections —
      // dismiss the dropdown immediately. The 'custom' path is multi-step and
      // debounced (text typing, dual-calendar, "Last N units") so leave it open.
      if (source !== 'custom') {
        setIsDropdownOpen(false)
      }
    },
    [onDateRangeChange, mostRecent],
  )

  // "Most recent" toggle: recompute the currently-active preset's range and
  // re-fire onDateRangeChange with the new intent. Never closes the dropdown
  // — this is a modifier on the current selection, not a terminal pick.
  const handleMostRecentChange = useCallback(
    (next: boolean) => {
      if (!selectedValue) return
      if (selectionSource !== 'shorthand' && selectionSource !== 'xtd') return
      const { startDate, endDate } = calculateDateRange(selectedValue)
      isInternalChangeRef.current = true
      onDateRangeChange(startDate, endDate, selectionSource, selectedValue, {
        mostRecent: next,
      })
    },
    [selectedValue, selectionSource, calculateDateRange, onDateRangeChange],
  )

  // Prior Period entry selection: always a fixed range, and always clears
  // "Most recent" (a named past period never auto-advances).
  const handlePriorPeriodSelect = useCallback(
    (groupKey: string, entry: PriorPeriodEntry) => {
      isInternalChangeRef.current = true
      setSelectionSource('priorPeriod')
      setSelectedValue(entry.key)
      onDateRangeChange(entry.startDate, entry.endDate, 'custom', null, {
        mostRecent: false,
        priorPeriod: { groupKey, entryKey: entry.key, label: entry.label },
      })
      setIsDropdownOpen(false)
    },
    [onDateRangeChange],
  )

  // Format the display text
  const getDisplayText = useCallback(() => {
    // If a shorthand option is selected, show its label
    if (selectionSource === 'shorthand' && selectedValue) {
      const option = shorthandOptions.find((o) => o.key === selectedValue)
      return option?.label || 'Select date'
    }

    // If an XTD option is selected, show its label
    if (selectionSource === 'xtd' && selectedValue) {
      const option = XTD_DATE_OPTIONS.find((o) => o.key === selectedValue)
      return option?.label || 'Select date'
    }

    // If a Prior Period entry is selected, show its named label
    if (selectionSource === 'priorPeriod' && selectedValue) {
      const entry = priorPeriods
        ?.flatMap((group) => group.entries)
        .find((e) => e.key === selectedValue)
      if (entry) return entry.label
    }

    // For custom or when no specific option is selected, show the date range.
    // Stored Dates are UTC instants — render them in UTC wall-clock so the
    // label matches what was stored (no viewer-offset shift).
    if (!dateRange.startDate || !dateRange.endDate) return 'Select date'

    return formatDateRangeLabel(dateRange.startDate, dateRange.endDate)
  }, [
    dateRange.startDate,
    dateRange.endDate,
    selectionSource,
    selectedValue,
    shorthandOptions,
    priorPeriods,
  ])

  // Whether the "Most recent" toggle should be interactive: disabled for
  // Custom, Prior Period, "All time", or (when the caller restricts it) a
  // preset that has no recomputable resolution.
  const mostRecentDisabled =
    selectionSource === 'custom' ||
    selectionSource === 'priorPeriod' ||
    selectionSource === null ||
    selectedValue === 'all' ||
    (mostRecentEligiblePresetKeys !== undefined &&
      (!selectedValue || !mostRecentEligiblePresetKeys.includes(selectedValue)))

  // Format the tooltip text to show actual date range
  const getTooltipText = useCallback(() => {
    if (!dateRange.startDate || !dateRange.endDate) return ''
    const start = dayjs(dateRange.startDate).utc()
    const end = dayjs(dateRange.endDate).utc()
    return `${start.format('MMM D, YYYY')} - ${end.format('MMM D, YYYY')}`
  }, [dateRange.startDate, dateRange.endDate])

  // Parse various date input formats
  const parseFlexibleDate = useCallback(
    (input: string): { date: Dayjs | null; error: string } => {
      if (!input.trim()) {
        return { date: null, error: 'Date is required' }
      }

      const trimmedInput = input.trim()
      const currentYear = dayjs.utc().year()

      // Try different date formats
      const formats = [
        // Standard formats
        'M/D/YYYY',
        'M/D/YY',
        'M/D',
        'MM/DD/YYYY',
        'MM/DD/YY',
        'MM/DD',

        // Month name formats
        'MMMM D, YYYY',
        'MMMM D YYYY',
        'MMMM D',
        'MMM D, YYYY',
        'MMM D YYYY',
        'MMM D',
      ]

      // Try parsing with each format
      for (const format of formats) {
        let testInput = trimmedInput

        // Handle short year format (25 -> 2025)
        if (format.includes('YY') && !format.includes('YYYY')) {
          testInput = trimmedInput.replace(/\/(\d{2})$/, (_, year) => {
            const yearNum = parseInt(year)
            const fullYear = yearNum < 50 ? 2000 + yearNum : 1900 + yearNum
            return `/${fullYear}`
          })
        }

        // Handle missing year (default to current year)
        if (
          !format.includes('Y') &&
          (testInput.includes('/') || testInput.match(/\b\w+\s+\d+$/))
        ) {
          testInput = `${testInput} ${currentYear}`
        }

        // Parse straight into UTC: a typed day ("May 15") has no timezone, so
        // it IS UTC midnight of that day — no conversion.
        const parsed = dayjs.utc(testInput, format, true)
        if (parsed.isValid()) {
          return { date: parsed, error: '' }
        }
      }

      // Try natural language parsing as fallback
      const naturalParsed = dayjs.utc(trimmedInput)
      if (naturalParsed.isValid()) {
        return { date: naturalParsed, error: '' }
      }

      return {
        date: null,
        error:
          'Invalid date format. Try: 5/5, 5/5/25, 5/5/2025, may 5, may 5 2025',
      }
    },
    [],
  )

  // Defaults to synced (no warning) when `onSync` is given but `isSynced`
  // isn't — callers that don't track sync state at all simply omit `onSync`.
  const showUnsyncedWarning = Boolean(onSync) && !(isSynced ?? true)

  // "Most recent" status icon: only for callers that pass `mostRecent` at all
  // (today, only dashboards-v2-1's header and tile pickers do — see
  // `mostRecent`'s own doc). Rolling shows an active icon; a preset that
  // COULD roll but currently doesn't (mostRecentDisabled is false — the same
  // gate the toggle itself uses) shows a locked icon the user can click to
  // re-enable. No icon for Custom / Prior Period / "All time" / ineligible
  // presets.
  const mostRecentIsRolling = mostRecent === true
  const mostRecentIsLockable = mostRecent === false && !mostRecentDisabled

  const handleEnableMostRecent = useCallback(() => {
    handleMostRecentChange(true)
    setIsDropdownOpen(true)
  }, [handleMostRecentChange])

  const mostRecentIconTooltip = mostRecentIsRolling ? (
    `Most recent period for ${getDisplayText()}`
  ) : (
    <span>
      Locked to prior window. To sync to current period,{' '}
      <MostRecentEnableLink
        as="u"
        onClick={(event) => {
          event.stopPropagation()
          handleEnableMostRecent()
        }}
      >
        enable
      </MostRecentEnableLink>
      .
    </span>
  )

  // The trigger tooltip: the plain date-range text alone when neither extra
  // line applies, or stacked above whichever of the Most-recent status line
  // and the unsynced-warning line apply (a tile can be both — e.g. its own
  // fixed "Most recent" desynced from the dashboard) — one Tooltip, up to
  // three lines, not multiple tooltips that swap depending on hover position.
  const showMostRecentStatusLine = mostRecentIsRolling || mostRecentIsLockable
  const dateTooltipText = getTooltipText()
  const tooltipTitle =
    showMostRecentStatusLine || showUnsyncedWarning ? (
      <TooltipLines>
        <span>{dateTooltipText}</span>
        {showMostRecentStatusLine && <span>{mostRecentIconTooltip}</span>}
        {showUnsyncedWarning && (
          <span>
            This tile uses different dates than the rest of the dashboard
          </span>
        )}
      </TooltipLines>
    ) : (
      dateTooltipText
    )

  return (
    <>
      {/* The menu renders in a portal but is still a React child of this
          trigger, so hover/focus from controls inside it would flash the
          tooltip over the open menu. */}
      <Tooltip content={tooltipTitle} side="right" disabled={isDropdownOpen}>
        <TooltipAnchor>
          <DatePopover
            open={isDropdownOpen}
            onOpenChange={(open) => setIsDropdownOpen(open)}
            side="bottom"
            align="start"
            aria-label="Date range"
            // The menu's controls (an antd calendar, a Select) portal their
            // own popups, so focus stays on the trigger.
            initialFocus={false}
            content={
              <CascadingDateMenu
                dateRange={dateRange}
                onDateRangeChange={handleDateChange}
                parseFlexibleDate={parseFlexibleDate}
                calculateDateRange={calculateDateRange}
                selectionSource={selectionSource}
                selectedValue={selectedValue}
                isOpen={isDropdownOpen}
                onSync={onSync}
                isSynced={isSynced}
                mostRecent={mostRecent}
                mostRecentDisabled={mostRecentDisabled}
                onMostRecentChange={handleMostRecentChange}
                priorPeriods={priorPeriods}
                onPriorPeriodSelect={handlePriorPeriodSelect}
              />
            }
          >
            <TriggerButton
              className="chart-date-selector-btn"
              $open={isDropdownOpen}
              $unsynced={showUnsyncedWarning}
              disabled={disabled}
            >
              {getDisplayText()}
              {(mostRecentIsRolling || mostRecentIsLockable) && (
                <MostRecentStatusIcon
                  $clickable={mostRecentIsLockable}
                  onClick={
                    mostRecentIsLockable
                      ? (event) => {
                          event.stopPropagation()
                          handleEnableMostRecent()
                        }
                      : undefined
                  }
                >
                  {mostRecentIsRolling ? (
                    <UpdateIcon fontSize="inherit" />
                  ) : (
                    <UpdateDisabledIcon fontSize="inherit" />
                  )}
                </MostRecentStatusIcon>
              )}
              {showUnsyncedWarning && (
                <UnsyncedWarningIcon>
                  <WarningRoundedIcon fontSize="inherit" />
                </UnsyncedWarningIcon>
              )}
              <TriggerChevron />
            </TriggerButton>
          </DatePopover>
        </TooltipAnchor>
      </Tooltip>
    </>
  )
}
